import {
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from '@carboncopyco/data';
import { ArchiveIcon } from '@carboncopyco/icons';
import type { Workspace } from '@carboncopyco/types';
import {
  ColorHex,
  MultiDropdownFilter,
  TextBase,
  notify,
} from '@carboncopyco/ui';
import { removeUndefinedValues } from '@carboncopyco/utils';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { WorkspaceAvatar } from '../Workspaces';

type ListItem = {
  id: string;
  label: Workspace['workspaceName'];
  color: Workspace['color'];
  isDeleted: Workspace['isDeleted'];
};

export const checkboxClasses = (checked) =>
  classNames(
    'rounded-sm border outline-none active:outline-none active:ring-0 active:ring-offset-0 focus:outline-none focus:ring-0 focus:ring-offset-0',
    checked
      ? 'hover:text-ds-primary-400 text-ds-primary active:text-ds-primary-500 cursor-pointer'
      : 'cursor-pointer text-ds-primary border-ds-natural-200 hover:border-ds-primary-400 active:border-ds-primary-500'
  );

const renderItem = ({ checked, onChange, item }) => (
  <div className="px-2 h-9 cursor-pointer flex items-center w-full gap-2 focus:bg-ds-natural-50 focus:outline-none focus:rounded-md">
    <div className="flex gap-3 items-center w-full">
      <input
        checked={checked}
        onChange={onChange}
        type="checkbox"
        className={checkboxClasses(checked)}
      />
      <div className="gap-2 flex items-center grow">
        {item.isDeleted ? (
          <ArchiveIcon color={ColorHex.natural600} />
        ) : (
          <WorkspaceAvatar
            size="small"
            workspace={{
              workspaceName: item.label,
              color: item.color,
              workspaceType: 'public',
            }}
          />
        )}
        <div className="truncate max-w-[110px]">
          <TextBase
            color="text-ds-natural-600"
            label={item.label}
            ellipsis
          />
        </div>
      </div>
    </div>
  </div>
);
export interface WorkspaceFilterProps {
  workspaces: Workspace[];
}
export const WorkspaceFilter = ({ workspaces }: WorkspaceFilterProps) => {
  const { organizationId } = useParams();
  const { data: notificationsSettings } = useGetUserSettingsQuery({
    organizationId,
  });
  const [updateNotificationsSettings] = useUpdateUserSettingsMutation();

  const items =
    workspaces
      ?.map(
        ({ id, workspaceName, color, isDeleted }): ListItem => ({
          id: String(id),
          label: isDeleted ? `${workspaceName} (archived)` : workspaceName,
          color,
          isDeleted,
        })
      )
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const selectedWorkspaces =
    notificationsSettings?.activityReport?.workspaces?.workspaceIds;

  const selectedItems = selectedWorkspaces
    ? Object.fromEntries(
        selectedWorkspaces.map((workspaceId) => [
          workspaceId,
          items.find((workspace) => {
            return workspace.id === String(workspaceId);
          })?.label,
        ])
      )
    : {};

  const handleSelectedWorkspaces = (workspaceId: Workspace['id']) => {
    const workspaceOptionsObject = Object.fromEntries(
      selectedWorkspaces.map((opt) => [opt, true])
    );
    workspaceOptionsObject[workspaceId] = !workspaceOptionsObject[workspaceId];

    const workspaceIdsToSend = Object.keys(
      removeUndefinedValues(workspaceOptionsObject)
    );

    updateNotificationsSettings({
      ...notificationsSettings,
      activityReport: {
        ...notificationsSettings?.activityReport,
        workspaces: {
          workspaceIds: workspaceIdsToSend,
          selectionMode: workspaceIdsToSend.length === 0 ? 'all' : 'custom',
        },
      },
    })
      .unwrap()
      .catch(() => {
        notify.error("Settings didn't update. Please try again.");
      });
  };

  const handleClearWorkspaces = () => {
    updateNotificationsSettings({
      ...notificationsSettings,
      activityReport: {
        ...notificationsSettings.activityReport,
        workspaces: {
          workspaceIds: [],
          selectionMode: 'all',
        },
      },
    })
      .unwrap()
      .catch(() => {
        notify.error("Settings didn't update. Please try again.");
      });
  };

  return (
    <MultiDropdownFilter
      capsuleWidth={216}
      contentWidth={216}
      rounded={false}
      renderItem={renderItem}
      searchableGroup={{ items }}
      selectedItems={selectedItems}
      onChange={handleSelectedWorkspaces}
      placeholder="All workspaces"
      onClear={handleClearWorkspaces}
      sideOffset={6}
      disabled={!notificationsSettings?.activityReport?.enabled}
    />
  );
};
